import React, { useState, useRef, useEffect } from "react";
import {
  FaComments,
  FaTimes,
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaSpinner,
  FaCamera,
  FaEdit,
  FaSave,
  FaTrash,
  FaTruck,
  FaBox,
  FaPlus,
  FaEnvelope,
  FaShoppingCart,
  FaSignOutAlt,
  FaQuestionCircle,
} from "react-icons/fa";
import axios from "axios";
import { useUser, useAuth, useClerk } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import emailjs from "emailjs-com";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SERVER_URL } from "./constants";

// Status steps for order tracking
const statusSteps = [
  { status: "OrderPlaced", label: "Order Placed", icon: <FaBox /> },
  { status: "Shipped", label: "Shipped", icon: <FaTruck /> },
  { status: "Out for Delivery", label: "Out for Delivery", icon: <FaTruck /> },
  { status: "Delivered", label: "Delivered", icon: <FaSave /> },
];

// Simple fuzzy matching function
const fuzzyMatch = (input, target) => {
  const cleanInput = input.toLowerCase().trim();
  const cleanTarget = target.toLowerCase();
  return cleanTarget.includes(cleanInput) || cleanInput.includes(cleanTarget);
};

// Intent recognition based on keywords
const recognizeIntent = (message) => {
  const msg = message.toLowerCase().trim();
  const intents = {
    profile: ["profile", "my profile", "account", "details", "edit profile"],
    orders: ["orders", "my orders", "track", "purchases", "history"],
    addresses: ["address", "addresses", "shipping", "delivery address"],
    contact: ["contact", "help me", "support", "message", "reach out"],
    cart: ["cart", "shopping cart", "basket", "items"],
    guide: ["guide", "how to", "tutorial", "start"],
    help: ["help", "what can you do", "options", "assist"],
    products: ["products", "shop", "items", "buy", "browse"],
    currency: ["currency", "money", "payment type", "rupees"],
    placeOrder: ["place order", "order", "buy now", "checkout"],
    deliveryTime: ["delivery", "shipping time", "when", "arrive"],
    trackOrder: ["track", "track order", "where", "status"],
    paymentOptions: ["payment", "pay", "options", "method"],
    returnPolicy: ["return", "refund", "policy", "cancel"],
    logout: ["logout", "sign out", "exit account", "leave"],
    exit: ["exit", "stop", "close", "done"],
    confirm: ["confirm", "yes", "sure", "ok"],
    cancel: ["cancel", "no", "nevermind", "back"],
  };

  for (const [intent, keywords] of Object.entries(intents)) {
    if (keywords.some((kw) => fuzzyMatch(msg, kw))) {
      return intent;
    }
  }
  return "unknown";
};

const Chatbot = () => {
  const { user } = useUser();
  const { getToken, signOut } = useAuth();
  const { signOut: clerkSignOut } = useClerk();
  const userId = user?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [mode, setMode] = useState(null);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: "",
    img_url: "",
  });
  const [editData, setEditData] = useState({ ...formData });
  const [isEditing, setIsEditing] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [originalPhone, setOriginalPhone] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [deleteConfirmOrderId, setDeleteConfirmOrderId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [deleteConfirmAddressId, setDeleteConfirmAddressId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);

  const [contactFormData, setContactFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    phone: "",
  });

  const initialAddressState = {
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    notes: "",
  };

  const botResponses = {
    greetings: [
      `Hey ${user?.firstName || "friend"}! I’m your Ecomify Buddy—here to help you shop, track, or tweak stuff! Say 'guide' to start or 'help' for my tricks!`,
    ],
    profile: [`Here’s your profile, ${user?.firstName || "you"}! Wanna edit it? Say 'edit' or snap a pic!`],
    orders: ["Your orders are lined up! Want to 'track' one or 'delete' something?"],
    addresses: ["Your shipping spots! Say 'add' for a new one, 'edit' to tweak, or 'delete' to drop!"],
    contact: ["Got a question? Fill this out and say 'send' when ready!"],
    cart: ["Checking your cart! (Still cooking—say 'guide' to shop for now!)"],
    guide: [
      "Let’s shop!\n1. 'products' to browse\n2. 'cart' to manage\n3. 'orders' for history\n4. 'profile' to update\n5. 'addresses' for delivery\nSay 'exit' anytime!",
    ],
    help: [
      "I’ve got you! Try these:\n- 'guide' to shop\n- 'profile' to update\n- 'orders' to track\n- 'addresses' for shipping\n- 'contact' to chat\n- 'cart' for fun\n- 'logout' to bounce\nOr just ask me anything!",
    ],
    currency: ["We use INR (Indian Rupees)—happy shopping!"],
    placeOrder: [
      "To order:\n1. Say 'products' to pick\n2. Add to 'cart'\n3. Checkout\n4. Pay with COD, Razorpay, or Crypto\n5. Done!",
    ],
    deliveryTime: ["Most orders arrive in 2-3 days after shipping—depends on your spot!"],
    trackOrder: ["Wanna track? Say 'orders' and pick one to follow!"],
    paymentOptions: ["Pay with Cash on Delivery, Razorpay (cards/UPI), or Crypto—your pick!"],
    returnPolicy: ["Not happy? Return defective/wrong items within 7 days—say 'contact' to start!"],
    products: ["Shopping time! (List coming soon!) Say 'cart' to add or 'guide' for more!"],
    logout: ["Wanna leave? Say 'confirm' to log out or 'cancel' to stay!"],
    exit: ["Taking a break! Say 'help' for options or chat away!"],
    unknown: [
      "Hmm, I’m lost! Try 'guide' to shop, 'help' for options, or tell me more below!",
      "Not sure what you mean! Say 'help' for tricks or spill it in the contact form!",
    ],
  };

  const getRandomResponse = (type) => {
    const responses = botResponses[type];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && !userId) {
      setMessages([{ type: "bot", content: "Hey there! Log in to unlock my full powers!" }]);
    } else if (isOpen && messages.length === 0) {
      handleBotResponse(getRandomResponse("greetings"));
    }
  }, [isOpen, userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (userId && mode === "profile") {
      fetchProfile();
    } else if (userId && mode === "orders") {
      fetchOrders();
    } else if (userId && mode === "addresses") {
      fetchAddresses();
    }
  }, [mode, userId]);

  useEffect(() => {
    if (location.pathname === "/login") {
      resetChatbot();
    }
  }, [location.pathname]);

  const resetChatbot = () => {
    setIsOpen(false);
    setMode(null);
    setIsEditing(false);
    setOtpStep(false);
    setOtpSent(false);
    setOtp("");
    setSelectedOrder(null);
    setTrackingStatus(null);
    setDeleteConfirmOrderId(null);
    setCurrentAddress(null);
    setDeleteConfirmAddressId(null);
    setMessages([]);
    setInputMessage("");
  };

  const handleBotResponse = (message) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { type: "bot", content: message }]);
      setIsTyping(false);
    }, 800);
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${SERVER_URL}/api/v1/user/${userId}`);
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        setFormData({
          firstName: userData.firstName || user?.firstName || "",
          lastName: userData.lastName || user?.lastName || "",
          phone: userData.phone || "",
          img_url: userData.img_url || "",
        });
        setEditData({
          firstName: userData.firstName || user?.firstName || "",
          lastName: userData.lastName || user?.lastName || "",
          phone: userData.phone || "",
          img_url: userData.img_url || "",
        });
        setOriginalPhone(userData.phone || "");
        handleBotResponse(`Hey ${user?.firstName || "you"}, your profile’s ready! Say 'edit' to tweak it!`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        handleBotResponse("No profile yet? Say 'edit' to set it up!");
      } else {
        setError("Oops, couldn’t fetch your profile!");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER_URL}/api/v1/orderu/${userId}`);
      setOrders(res.data.data || []);
      handleBotResponse("Your orders are here! Say 'track' or 'delete' to manage them!");
    } catch (error) {
      setError("Failed to fetch orders.");
      handleBotResponse("Orders didn’t load—say 'try again' to retry!");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderStatus = async (orderId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER_URL}/api/v1/ordertrack/${orderId}`);
      if (res.data.m === "f" || !res.data.data) {
        setError("Order not found.");
        handleBotResponse("That order’s hiding! Pick another or say 'exit'!");
      } else {
        setTrackingStatus(res.data.data.delivaryStatus);
        handleBotResponse("Tracking’s up—check it below!");
      }
    } catch (error) {
      setError("Error fetching order status.");
      handleBotResponse("Tracking’s shy—say 'try again' to retry!");
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    setLoading(true);
    const token = await getToken();
    try {
      const response = await axios.get(`${SERVER_URL}/api/v1/address/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = response.data;
      if (result.success) {
        setAddresses(result.addresses || []);
        handleBotResponse("Your addresses are set! Say 'add', 'edit', or 'delete'!");
      } else {
        setError(result.message);
        handleBotResponse(result.message);
      }
    } catch (error) {
      setError("Failed to fetch addresses.");
      handleBotResponse("Addresses didn’t load—say 'try again'!");
    } finally {
      setLoading(false);
    }
  };

  const handleUserMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !userId) return;

    setMessages((prev) => [...prev, { type: "user", content: inputMessage }]);
    const intent = recognizeIntent(inputMessage);
    setInputMessage("");

    switch (intent) {
      case "profile":
        setMode("profile");
        resetSubModes();
        handleBotResponse(getRandomResponse("profile"));
        break;
      case "orders":
        setMode("orders");
        resetSubModes();
        handleBotResponse(getRandomResponse("orders"));
        break;
      case "addresses":
        setMode("addresses");
        resetSubModes();
        handleBotResponse(getRandomResponse("addresses"));
        break;
      case "contact":
        setMode("contact");
        resetSubModes();
        handleBotResponse(getRandomResponse("contact"));
        break;
      case "cart":
        setMode("cart");
        resetSubModes();
        handleBotResponse(getRandomResponse("cart"));
        break;
      case "guide":
        setMode("guide");
        resetSubModes();
        handleBotResponse(getRandomResponse("guide"));
        break;
      case "help":
        handleBotResponse(getRandomResponse("help"));
        break;
      case "products":
        handleBotResponse(getRandomResponse("products"));
        break;
      case "currency":
        handleBotResponse(getRandomResponse("currency"));
        break;
      case "placeOrder":
        handleBotResponse(getRandomResponse("placeOrder"));
        break;
      case "deliveryTime":
        handleBotResponse(getRandomResponse("deliveryTime"));
        break;
      case "trackOrder":
        if (mode === "orders" && orders.length > 0 && !selectedOrder) {
          handleBotResponse("Pick an order to track below!");
        } else {
          handleBotResponse(getRandomResponse("trackOrder"));
        }
        break;
      case "paymentOptions":
        handleBotResponse(getRandomResponse("paymentOptions"));
        break;
      case "returnPolicy":
        handleBotResponse(getRandomResponse("returnPolicy"));
        break;
      case "logout":
        setMode("logout");
        handleBotResponse(getRandomResponse("logout"));
        break;
      case "exit":
        setMode(null);
        resetSubModes();
        handleBotResponse(getRandomResponse("exit"));
        break;
      case "confirm":
        if (mode === "logout") {
          await clerkSignOut();
          resetChatbot();
          navigate("/login");
          handleBotResponse("Logged out! See ya soon!");
        } else if (deleteConfirmOrderId) {
          handleDeleteOrderConfirm();
        } else if (deleteConfirmAddressId) {
          handleDeleteAddressConfirm();
        } else {
          handleBotResponse("What are we confirming? Say more!");
        }
        break;
      case "cancel":
        if (mode === "logout") {
          handleBotResponse("Staying? Cool—what’s next?");
        } else if (deleteConfirmOrderId) {
          handleDeleteOrderCancel();
        } else if (deleteConfirmAddressId) {
          handleDeleteAddressCancel();
        } else {
          handleBotResponse("Cancel what? Tell me more!");
        }
        break;
      case "unknown":
        if (mode) {
          handleBotResponse(
            `You’re in ${mode} mode! ${
              mode === "profile"
                ? "Say 'edit' to tweak!"
                : mode === "orders"
                ? "Say 'track' or 'delete'!"
                : mode === "addresses"
                ? "Say 'add', 'edit', or 'delete'!"
                : mode === "contact"
                ? "Fill it out and say 'send'!"
                : mode === "cart"
                ? "Cart’s in progress—say 'guide'!"
                : "Follow the guide or say 'exit'!"
            }`
          );
        } else {
          setMode("contact");
          handleBotResponse(getRandomResponse("unknown"));
        }
        break;
      default:
        handleBotResponse("I’m not sure what you mean! Say 'help' for options!");
        break;
    }
  };

  const resetSubModes = () => {
    setSelectedOrder(null);
    setTrackingStatus(null);
    setDeleteConfirmOrderId(null);
    setCurrentAddress(null);
    setDeleteConfirmAddressId(null);
    setIsEditing(false);
    setOtpStep(false);
    setOtpSent(false);
    setOtp("");
  };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(editData.phone)) {
      toast.error("Enter a valid 10-digit phone number!");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/v1/send-otp`, {
        phone: `+91${editData.phone}`,
      });
      if (response.data.success) {
        setOtpSent(true);
        toast.success("OTP sent!");
        handleBotResponse("OTP sent! Check your phone and type it in!");
      } else {
        toast.error("Failed to send OTP!");
        handleBotResponse("OTP didn’t send—try again?");
      }
    } catch (error) {
      toast.error("Error sending OTP!");
      handleBotResponse("Something broke—retry sending OTP?");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Enter a valid 6-digit OTP!");
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${SERVER_URL}/api/v1/verify-otp`, {
        phone: `+91${editData.phone}`,
        otp,
      });
      if (response.data.success) {
        await handleSaveProfile();
        toast.success("Phone verified and profile updated!");
        handleBotResponse("Phone’s good! Profile updated!");
        setOtpStep(false);
        setOtpSent(false);
        setOtp("");
        setOriginalPhone(editData.phone);
      } else {
        toast.error("Invalid OTP!");
        handleBotResponse("OTP didn’t work—try again!");
      }
    } catch (error) {
      toast.error("OTP verification failed!");
      handleBotResponse("Verification flopped—retry?");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const payload = { userId, ...editData };
      const response = await axios({
        method: formData.phone || formData.img_url ? "put" : "post",
        url: formData.phone || formData.img_url ? `${SERVER_URL}/api/v1/user/${userId}` : `${SERVER_URL}/api/v1/user`,
        data: payload,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setFormData({ ...editData });
        setIsEditing(false);
        setOtpStep(false);
        handleBotResponse("Profile saved! You’re all set!");
      }
    } catch (err) {
      setError("Error saving profile.");
      handleBotResponse("Save failed—try again?");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = () => {
    if (!editData.firstName || !editData.lastName) {
      toast.error("First and last name are required!");
      return;
    }
    if (editData.phone && !/^\d{10}$/.test(editData.phone)) {
      toast.error("Enter a valid 10-digit phone number!");
      return;
    }

    if (editData.phone && editData.phone !== originalPhone) {
      setOtpStep(true);
      sendOtp();
    } else {
      handleSaveProfile();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large—keep it under 5MB!");
      return;
    }

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("Use JPEG or PNG only!");
      return;
    }

    const imageData = new FormData();
    imageData.append("file", file);
    imageData.append("upload_preset", "unsigned_preset");

    setLoading(true);
    try {
      const response = await axios.post(
        "https://api.cloudinary.com/v1_1/dkzwhqhbr/image/upload",
        imageData
      );
      setEditData((prev) => ({ ...prev, img_url: response.data.secure_url }));
      toast.success("Photo uploaded!");
      handleBotResponse("Photo’s up! Save it when ready!");
    } catch (err) {
      setError("Upload failed.");
      toast.error("Upload didn’t work—retry?");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrderInitiate = (orderId) => {
    setDeleteConfirmOrderId(orderId);
    handleBotResponse("Sure about deleting this order? Say 'confirm' or 'cancel'!");
  };

  const handleDeleteOrderConfirm = async () => {
    setLoading(true);
    try {
      await axios.delete(`${SERVER_URL}/api/v1/orderd/${deleteConfirmOrderId}`);
      setOrders((prev) => prev.filter((order) => order._id !== deleteConfirmOrderId));
      setDeleteConfirmOrderId(null);
      handleBotResponse("Order deleted! What’s next?");
    } catch (error) {
      setError("Failed to delete order.");
      handleBotResponse("Delete didn’t work—retry?");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrderCancel = () => {
    setDeleteConfirmOrderId(null);
    handleBotResponse("Order stays! What else?");
  };

  const handleTrackOrder = (order) => {
    setSelectedOrder(order);
    setDeleteConfirmOrderId(null);
    fetchOrderStatus(order._id);
  };

  const handleEditToggle = (type, address = null) => {
    setIsEditing(true);
    if (type === "profile") {
      handleBotResponse("Edit your profile—say 'save' when done!");
    } else if (type === "address") {
      setCurrentAddress(address || initialAddressState);
      handleBotResponse(address ? "Edit this address!" : "Add a new address!");
    }
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "pincode") {
      formattedValue = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "phone") {
      formattedValue = value.replace(/\D/g, "").slice(0, 10);
    }
    setCurrentAddress((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleSaveAddress = async () => {
    setLoading(true);
    const token = await getToken();
    try {
      const payload = { ...currentAddress, userId };
      const response = await axios({
        method: currentAddress._id ? "put" : "post",
        url: currentAddress._id
          ? `${SERVER_URL}/api/v1/address/${currentAddress._id}`
          : `${SERVER_URL}/api/v1/address`,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        data: payload,
      });
      const result = response.data;
      if (result.success) {
        if (currentAddress._id) {
          setAddresses((prev) =>
            prev.map((addr) => (addr._id === currentAddress._id ? result.address : addr))
          );
        } else {
          setAddresses((prev) => [...prev, result.address]);
        }
        setCurrentAddress(null);
        setIsEditing(false);
        handleBotResponse("Address saved—ready to ship!");
      } else {
        setError(result.message);
        handleBotResponse(result.message);
      }
    } catch (error) {
      setError("Failed to save address.");
      handleBotResponse("Address didn’t save—retry?");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddressInitiate = (addressId) => {
    setDeleteConfirmAddressId(addressId);
    handleBotResponse("Delete this address? Say 'confirm' or 'cancel'!");
  };

  const handleDeleteAddressConfirm = async () => {
    setLoading(true);
    const token = await getToken();
    try {
      const response = await axios.delete(`${SERVER_URL}/api/v1/address/${deleteConfirmAddressId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = response.data;
      if (result.success) {
        setAddresses((prev) => prev.filter((addr) => addr._id !== deleteConfirmAddressId));
        setDeleteConfirmAddressId(null);
        handleBotResponse("Address gone! What’s next?");
      } else {
        setError(result.message);
        handleBotResponse(result.message);
      }
    } catch (error) {
      setError("Failed to delete address.");
      handleBotResponse("Delete failed—retry?");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddressCancel = () => {
    setDeleteConfirmAddressId(null);
    handleBotResponse("Address stays! What now?");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setEditData((prev) => ({ ...prev, [name]: value.replace(/\D/g, "").slice(0, 10) }));
    } else {
      setEditData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleContactFormChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(contactFormData.phone)) {
      setLoading(false);
      handleBotResponse("Phone needs to be 10 digits!");
      return;
    }

    emailjs
      .sendForm("service_6ztxc8g", "template_gqh7qxu", formRef.current, "yVNXoiryRF-4YH2Mi")
      .then(
        () => {
          setLoading(false);
          toast.success("Message sent!");
          setContactFormData({ name: "", email: "", subject: "", message: "", phone: "" });
          handleBotResponse("Message sent! We’ll get back to you soon!");
        },
        (error) => {
          setLoading(false);
          handleBotResponse("Message didn’t send—try again?");
        }
      );
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-4 z-50 p-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
      >
        {isOpen ? <FaTimes className="w-6 h-6" /> : <FaComments className="w-6 h-6 animate-pulse" />}
      </button>

      <div
        className={`fixed bottom-36 right-4 z-50 transition-all duration-300 transform ${
          isOpen ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="w-80 sm:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <FaRobot className="w-5 h-5 text-white animate-wiggle" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Ecomify Buddy</h3>
                  <p className="text-xs text-emerald-100">
                    {mode ? `${mode.charAt(0).toUpperCase() + mode.slice(1)} Mode` : "Ready to Assist!"}
                  </p>
                </div>
              </div>
              <button
                onClick={resetChatbot}
                className="text-white/80 hover:text-white transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="h-96 overflow-y-auto p-4 bg-gray-50">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className="flex items-end gap-2 max-w-[80%]">
                    {message.type === "bot" && (
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
                        <FaRobot className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl p-3 ${
                        message.type === "user"
                          ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-br-none shadow-md"
                          : "bg-white shadow-md rounded-bl-none"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.type === "user" && (
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
                        <FaUser className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <FaRobot className="w-3 h-3 text-white" />
                  </div>
                  <div className="bg-white rounded-2xl p-3 shadow-md rounded-bl-none">
                    <FaSpinner className="w-4 h-4 animate-spin text-emerald-500" />
                  </div>
                </div>
              )}
              {!userId && (
                <div className="bg-white p-4 rounded-lg shadow-md text-center">
                  <p className="text-sm font-semibold mb-2">Hello, Guest!</p>
                  <p className="text-sm text-gray-600 mb-3">
                    Log in to unlock all my features!
                  </p>
                  <button
                    onClick={() => navigate("/login")}
                    className="flex items-center gap-1 px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors mx-auto shadow-sm hover:shadow-md"
                  >
                    <FaUser />
                    <span>Log In</span>
                  </button>
                </div>
              )}
              {userId && mode === "profile" && !otpStep && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Your Profile:</p>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-gray-600">First Name:</label>
                        <input
                          type="text"
                          name="firstName"
                          value={editData.firstName}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                          placeholder="Your first name"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Last Name:</label>
                        <input
                          type="text"
                          name="lastName"
                          value={editData.lastName}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                          placeholder="Your last name"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Phone:</label>
                        <div className="flex">
                          <span className="inline-flex items-center px-2 py-1 border border-r-0 border-gray-300 rounded-l-md bg-gray-100 text-gray-600 text-sm">
                            +91
                          </span>
                          <input
                            type="tel"
                            name="phone"
                            value={editData.phone}
                            onChange={handleInputChange}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded-r-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                            placeholder="10-digit phone"
                            maxLength="10"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Photo:</label>
                        {editData.img_url ? (
                          <img src={editData.img_url} alt="Profile" className="w-16 h-16 rounded-lg shadow-md" />
                        ) : (
                          <p className="text-sm text-gray-400">No photo—add one!</p>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditData({ ...formData });
                          }}
                          className="px-3 py-1 bg-gray-300 rounded-md hover:bg-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleProfileSubmit}
                          disabled={loading}
                          className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md"
                        >
                          {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                          <span>Save</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm">First Name: {formData.firstName || "Not set"}</p>
                      <p className="text-sm">Last Name: {formData.lastName || "Not set"}</p>
                      <p className="text-sm">Phone: {formData.phone || "Not set"}</p>
                      {formData.img_url && (
                        <img src={formData.img_url} alt="Profile" className="w-16 h-16 rounded-lg mt-2 shadow-md" />
                      )}
                      <button
                        onClick={() => handleEditToggle("profile")}
                        className="mt-3 flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md"
                      >
                        <FaEdit />
                        <span>Edit</span>
                      </button>
                    </div>
                  )}
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "profile" && otpStep && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Verify Phone</p>
                  <p className="text-sm text-gray-600 mb-3">
                    OTP sent to +91{editData.phone}
                  </p>
                  {!otpSent ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Verify your new number: +91{editData.phone}
                      </p>
                      <button
                        onClick={sendOtp}
                        disabled={otpLoading}
                        className="w-full flex items-center justify-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md disabled:bg-gray-400"
                      >
                        {otpLoading ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                        <span>{otpLoading ? "Sending..." : "Send OTP"}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        maxLength="6"
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm text-center text-lg tracking-widest"
                        placeholder="••••••"
                      />
                      <button
                        onClick={verifyOtp}
                        disabled={otpLoading || otp.length !== 6}
                        className="w-full flex items-center justify-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md disabled:bg-gray-400"
                      >
                        {otpLoading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                        <span>{otpLoading ? "Verifying..." : "Verify OTP"}</span>
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setOtpStep(false);
                      setOtpSent(false);
                      setOtp("");
                      setEditData((prev) => ({ ...prev, phone: originalPhone }));
                    }}
                    className="w-full mt-2 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {userId && mode === "orders" && !selectedOrder && !deleteConfirmOrderId && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Your Orders:</p>
                  {orders.length === 0 ? (
                    <p className="text-sm text-gray-600">No orders yet—say 'guide' to shop!</p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {orders.map((order) => (
                        <div key={order._id} className="flex items-center gap-2 border-b pb-2">
                          <img
                            src={order.image}
                            alt={order.name}
                            className="w-12 h-12 rounded-lg object-cover shadow-sm"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {order.name} ×{order.quantity}
                            </p>
                            <p className="text-xs text-gray-600">₹{order.totalAmount.toFixed(2)}</p>
                          </div>
                          <button
                            onClick={() => handleTrackOrder(order)}
                            className="p-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                          >
                            <FaTruck />
                          </button>
                          <button
                            onClick={() => handleDeleteOrderInitiate(order._id)}
                            className="p-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "orders" && !selectedOrder && deleteConfirmOrderId && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Confirm Delete</p>
                  <p className="text-sm text-gray-600 mb-3">Delete this order?</p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleDeleteOrderCancel}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors shadow-sm"
                    >
                      <FaTimes />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleDeleteOrderConfirm}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors shadow-sm hover:shadow-md"
                    >
                      {loading ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                      <span>Confirm</span>
                    </button>
                  </div>
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "orders" && selectedOrder && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Tracking: {selectedOrder.name}</p>
                  {loading ? (
                    <p className="text-sm">Fetching status...</p>
                  ) : trackingStatus ? (
                    <div className="space-y-2">
                      {statusSteps.map((step, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div
                            className={`w-6 h-6 flex items-center justify-center rounded-full ${
                              step.status === trackingStatus ||
                              statusSteps.findIndex((s) => s.status === trackingStatus) >= index
                                ? "bg-green-500 text-white"
                                : "bg-gray-300 text-gray-600"
                            }`}
                          >
                            {step.icon}
                          </div>
                          <span className="text-sm">{step.label}</span>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          setSelectedOrder(null);
                          setTrackingStatus(null);
                        }}
                        className="mt-3 flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors shadow-sm hover:shadow-md"
                      >
                        <FaTimes />
                        <span>Close</span>
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-red-500">Status unavailable</p>
                  )}
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "addresses" && !currentAddress && !deleteConfirmAddressId && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-semibold">Your Addresses:</p>
                    <button
                      onClick={() => handleEditToggle("address")}
                      className="flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md"
                    >
                      <FaPlus />
                      <span>Add</span>
                    </button>
                  </div>
                  {addresses.length === 0 ? (
                    <p className="text-sm text-gray-600">No addresses—add one now!</p>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {addresses.map((address) => (
                        <div key={address._id} className="flex items-center gap-2 border-b pb-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{address.name}</p>
                            <p className="text-xs text-gray-600">
                              {address.address}, {address.city}, {address.state} - {address.pincode}
                            </p>
                            <p className="text-xs text-gray-600">📱 {address.phone}</p>
                          </div>
                          <button
                            onClick={() => handleEditToggle("address", address)}
                            className="p-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteAddressInitiate(address._id)}
                            className="p-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "addresses" && currentAddress && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">
                    {currentAddress._id ? "Edit Address" : "New Address"}:
                  </p>
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="name"
                      value={currentAddress.name}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Full Name"
                    />
                    <input
                      type="email"
                      name="email"
                      value={currentAddress.email}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      name="address"
                      value={currentAddress.address}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Street Address"
                    />
                    <input
                      type="text"
                      name="city"
                      value={currentAddress.city}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      name="state"
                      value={currentAddress.state}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="State"
                    />
                    <input
                      type="text"
                      name="pincode"
                      value={currentAddress.pincode}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="6-digit Pincode"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={currentAddress.phone}
                      onChange={handleAddressInputChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="10-digit Phone"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setCurrentAddress(null)}
                        className="px-3 py-1 bg-gray-300 rounded-md hover:bg-gray-400 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAddress}
                        disabled={loading}
                        className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md"
                      >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                        <span>Save</span>
                      </button>
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "addresses" && !currentAddress && deleteConfirmAddressId && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Confirm Delete</p>
                  <p className="text-sm text-gray-600 mb-3">Remove this address?</p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleDeleteAddressCancel}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors shadow-sm"
                    >
                      <FaTimes />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={handleDeleteAddressConfirm}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors shadow-sm hover:shadow-md"
                    >
                      {loading ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                      <span>Confirm</span>
                    </button>
                  </div>
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "contact" && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Contact Us!</p>
                  <form ref={formRef} onSubmit={handleContactSubmit} className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      value={contactFormData.name}
                      onChange={handleContactFormChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Your Name"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactFormChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Your Email"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={contactFormData.phone}
                      onChange={handleContactFormChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="10-digit Phone"
                      required
                    />
                    <input
                      type="text"
                      name="subject"
                      value={contactFormData.subject}
                      onChange={handleContactFormChange}
                      className="w-full px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Subject"
                      required
                    />
                    <textarea
                      name="message"
                      value={contactFormData.message}
                      onChange={handleContactFormChange}
                      className="w-full px-2 py-1 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                      placeholder="Your Message"
                      rows="3"
                      required
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors shadow-sm hover:shadow-md"
                      >
                        {loading ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                        <span>Send</span>
                      </button>
                    </div>
                  </form>
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                </div>
              )}
              {userId && mode === "cart" && (
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <p className="text-sm font-semibold mb-2">Your Cart</p>
                  <p className="text-sm text-gray-600">
                    Cart’s coming soon! Say 'products' to shop or 'guide' for more!
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <form onSubmit={handleUserMessage} className="p-4 bg-white border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={
                  mode
                    ? `In ${mode} mode—say 'help' or 'exit'`
                    : "Ask me anything—'guide' or 'help' to start!"
                }
                className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
                disabled={loading || !userId}
              />
              {userId && mode === "profile" && !isEditing && !otpStep && (
                <label className="p-2 rounded-full text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 cursor-pointer transition-colors">
                  <FaCamera className="w-5 h-5" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={loading}
                  />
                </label>
              )}
              <button
                type="submit"
                disabled={!inputMessage.trim() || loading || !userId}
                className={`p-2 rounded-full ${
                  inputMessage.trim() && !loading && userId
                    ? "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                    : "text-gray-400"
                }`}
              >
                <FaPaperPlane className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
